import { FC, useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui";
import { useParams } from "react-router-dom";
import {
  useGetSchoolByIdQuery,
  useGetSchoolCommercialBySchoolIdQuery,
} from "@/services";
import logo from "@/assets/logoLight.png";
import axiosAuth from "@/services/axiosAuth";
import { FILE_UPLOAD } from "@/services/school/apiRoutes";

interface SchoolSummaryCardProps {
  schoolId?: string;
}

const Field: FC<{
  label: string;
  value?: string | number | boolean | null;
}> = ({ label, value }) => {
  const text =
    value === null || value === undefined || value === "" ? "-" : String(value);
  return (
    <div className="flex flex-col gap-[2px] min-w-0">
      <span className="text-[13px] text-gray-400">{label}</span>
      <span
        className="text-[15px] font-semibold text-white truncate"
        title={text}
      >
        {text}
      </span>
    </div>
  );
};

export const SchoolSummaryCard: FC<SchoolSummaryCardProps> = ({ schoolId }) => {
  const params = useParams();
  const id = schoolId || (params?.id as string | undefined);

  const { data: schoolRes, isPending: isSchoolLoading } = useGetSchoolByIdQuery(
    id || "",
    { enabled: Boolean(id) } as any
  );
  const { data: commercialRes, isPending: isCommLoading } =
    useGetSchoolCommercialBySchoolIdQuery(id || "", {
      enabled: Boolean(id),
    } as any);

  const school = (schoolRes as any)?.data || {};
  const commercial = (commercialRes as any)?.data || {};
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const logoUrlRef = useRef<string | null>(null);

  const isLoading = isSchoolLoading || isCommLoading;

  // Fetch logo when schoolLogo ID is available
  useEffect(() => {
    const fetchLogo = async () => {
      // Clean up previous URL if it exists
      if (logoUrlRef.current) {
        window.URL.revokeObjectURL(logoUrlRef.current);
        logoUrlRef.current = null;
      }

      if (school?.schoolLogo) {
        try {
          const response = await axiosAuth.get(
            `${FILE_UPLOAD}${school.schoolLogo}`,
            {
              responseType: "blob",
            }
          );

          if (response?.status === 200) {
            const blob = new Blob([response.data], {
              type: response.headers["content-type"] || "image/png",
            });
            const url = window.URL.createObjectURL(blob);
            logoUrlRef.current = url;
            setLogoUrl(url);
          }
        } catch (error) {
          console.error("Error fetching school logo:", error);
          setLogoUrl(null);
        }
      } else {
        setLogoUrl(null);
      }
    };

    fetchLogo();

    // Cleanup: revoke object URL when component unmounts or logo changes
    return () => {
      if (logoUrlRef.current) {
        window.URL.revokeObjectURL(logoUrlRef.current);
        logoUrlRef.current = null;
      }
    };
  }, [school?.schoolLogo]);

  return (
    <Card className="bg-[#3c352c] rounded-xl p-5 mt-6 shadow-md">
      {isLoading && <div className="py-2 text-white">Loading...</div>}
      {!isLoading && (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left block with logo + name */}
          <div className="flex flex-col items-center justify-center gap-3 lg:w-[220px]">
            <div className="h-24 w-24 rounded-full bg-white flex items-center justify-center overflow-hidden p-1">
              {/* Replace with dynamic school logo if available */}
              <img
                src={logoUrl || logo}
                alt="School Logo"
                className="w-full h-full object-contain rounded-full"
              />
            </div>
            <h2 className="text-lg font-semibold text-white text-center">
              {school?.schoolName || "School Name"}
            </h2>
          </div>

          {/* Right block with details */}
          <div className="flex-1 bg-[#6a5540] rounded-lg p-6 grid grid-cols-1 md:grid-cols-3 gap-y-6 gap-x-8">
            {/* Column 1 */}
            <div className="flex flex-col gap-5">
              <Field label="City" value={school?.city} />
              <Field label="Phone No." value={school?.phoneNumber} />
              <Field label="Email ID" value={school?.emailId} />
              <Field label="Educator type" value={school?.educatorType} />
            </div>

            {/* Column 2 */}
            <div className="flex flex-col gap-5 border-l border-gray-500 pl-6">
              <Field label="# Faculty Days" value={school?.facultyDays} />
              <Field
                label="Grades Selected"
                value={school?.gradesSelectedData?.map(
                  (i: any) => " " + i.curriculum_grade + " "
                )}
              />
              <Field label="SKU" value={school?.sku} />
            </div>

            {/* Column 3 */}
            <div className="flex flex-col gap-5 border-l border-gray-500 pl-6">
              <Field label="CRM Ops Status" value={commercial?.crmOpsStatus} />
              <Field label="Target Date" value={commercial?.targetDate} />
              <Field
                label="Lab Setup Status"
                value={commercial?.labSetupStatus}
              />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default SchoolSummaryCard;
